import { useEffect } from "react";
import {
  clampColumnCount,
  MAX_CONTENT_COLUMNS,
} from "@/lib/cms-puck/blocks/column-count";
import { Slider } from "@/components/ui/slider";
import type { CustomField } from "@measured/puck";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";

type ColumnCountSliderProps = {
  label?: string;
  value: unknown;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  /** Si `value` est absent / invalide — défaut = `min`. */
  fallback?: number;
  /** Persiste le fallback quand la prop n'est pas encore dans le layout. */
  persistFallback?: boolean;
};

export function ColumnCountSliderField({
  label,
  value,
  onChange,
  min = 1,
  max = MAX_CONTENT_COLUMNS,
  fallback,
  persistFallback = false,
}: ColumnCountSliderProps) {
  const cmsNew = useCmsNew();
  const resolvedLabel =
    label ?? cmsNew("cms.new.fields.columnCount.label", "Colonnes");
  const resolvedFallback = fallback ?? min;
  const current = clampColumnCount(value, {
    min,
    max,
    fallback: resolvedFallback,
  });

  // Blocs existants sans `columns` : ancrer le défaut (ex. fiche rubrique = 4).
  useEffect(() => {
    if (!persistFallback) return;
    const n = Math.floor(Number(value));
    if (!Number.isFinite(n)) onChange(resolvedFallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync une fois valeur absente
  }, [persistFallback, resolvedFallback, value]);

  return (
    <div className="space-y-3 p-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-slate-600">
          {resolvedLabel}
        </span>
        <span className="tabular-nums text-xs font-semibold text-slate-800">
          {current}
        </span>
      </div>
      <Slider
        min={min}
        max={max}
        step={1}
        value={[current]}
        onValueChange={(next) => onChange(next[0] ?? resolvedFallback)}
        aria-label={resolvedLabel}
      />
      <p className="text-[11px] leading-snug text-slate-400">
        {cmsNew("cms.new.fields.columnCount.hintBefore", "De")} {min}{" "}
        {cmsNew("cms.new.fields.columnCount.hintTo", "à")} {max}{" "}
        {cmsNew(
          "cms.new.fields.columnCount.hintAfter",
          "colonnes (limite CSS du thème).",
        )}
      </p>
    </div>
  );
}

/** Fabrique un champ Puck custom pour le nombre de colonnes. */
export function createColumnCountField<T extends number = number>(
  label: string,
  min: number,
  max: number,
  opts?: { fallback?: number; persistFallback?: boolean },
): CustomField<T> {
  return {
    type: "custom",
    label,
    render: ({ value, onChange }) => (
      <ColumnCountSliderField
        label={label}
        value={value}
        onChange={(next) => onChange(next as T)}
        min={min}
        max={max}
        fallback={opts?.fallback}
        persistFallback={opts?.persistFallback}
      />
    ),
  };
}
